import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import api, { getApiErrorMessage } from '../../api';
import { notifyCountsChanged } from '../../utils/counts';
import AppShell from '../AppShell';
import ThemePicker from '../ThemePicker';
import { qaParticipantUrl } from './useQa';
import { copyToClipboard, questionsWord } from './qaUtils';
import './Qa.css';

const todayTitle = () => `Вопросы — ${new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}`;

/** Заглушка для бесплатного тарифа: что это и где включается. */
export function QaLocked({ message }) {
    const navigate = useNavigate();
    return (
        <div className="qa-sessions">
            <div className="qa-card qa-locked">
                <div className="qa-locked__icon" aria-hidden><svg viewBox="0 0 24 24"><path d="M21 12a8 8 0 01-11.6 7.1L4 20l1-4.6A8 8 0 1121 12z" /><path d="M10 9.5a2 2 0 113 1.7c-.6.4-1 .8-1 1.5M12 15h.01" /></svg></div>
                <h2>Вопросы из зала</h2>
                <p>
                    Участники сканируют QR-код и задают вопросы спикеру без регистрации, голосуют за самые интересные,
                    а вы выводите лучшие на экран и отмечаете отвеченные.
                </p>
                <p className="qa-hint">{message || 'Доступно на тарифах «Расширенный» и «Корпоративный».'}</p>
                <button type="button" className="btn btn--primary" onClick={() => navigate('/profile#tariffs')}>Выбрать тариф</button>
            </div>
        </div>
    );
}

function CreateModal({ onClose, onCreated }) {
    const [title, setTitle] = useState(todayTitle);
    const [enabled, setEnabled] = useState(true);
    const [premoderation, setPremoderation] = useState(false);
    const [aiModeration, setAiModeration] = useState(true);
    const [theme, setTheme] = useState('light');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const res = await api.post('/qa-sessions', { title, enabled, premoderation, ai_moderation: aiModeration, theme });
            notifyCountsChanged();
            onCreated(res.data);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось создать сессию'));
            setBusy(false);
        }
    };

    return (
        <div className="qa-modal-overlay" role="presentation" onClick={() => !busy && onClose()}>
            <form className="qa-modal" role="dialog" aria-modal="true" aria-labelledby="qa-create-title" onSubmit={submit} onClick={(e) => e.stopPropagation()}>
                <h2 id="qa-create-title">Новая сессия вопросов</h2>
                <label className="qa-field">
                    <span>Название</span>
                    <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} autoFocus required placeholder="Например, «Лекция по маркетингу»" />
                    <small>Участники увидят его над полем для вопроса.</small>
                </label>
                <label className="qa-check">
                    <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                    <span>Сразу открыть приём вопросов<small>Можно открыть позже — например, перед началом выступления.</small></span>
                </label>
                <label className="qa-check">
                    <input type="checkbox" checked={premoderation} onChange={(e) => setPremoderation(e.target.checked)} />
                    <span>Премодерация<small>Вопрос увидят все только после вашего одобрения.</small></span>
                </label>
                <label className="qa-check">
                    <input type="checkbox" checked={aiModeration} onChange={(e) => setAiModeration(e.target.checked)} />
                    <span>Автомодерация<small>Оскорбления и спам отклоняются автоматически.</small></span>
                </label>
                <ThemePicker id="qa-create-theme" value={theme} onChange={setTheme} />
                {error && <p className="qa-form-error" role="alert">{error}</p>}
                <div className="qa-modal__footer">
                    <button type="button" className="btn btn--secondary" onClick={onClose} disabled={busy}>Отмена</button>
                    <button type="submit" className="btn btn--primary" disabled={busy || !title.trim()}>{busy ? 'Создаём…' : 'Создать и открыть'}</button>
                </div>
            </form>
        </div>
    );
}

function QrModal({ session, onClose }) {
    const link = qaParticipantUrl(session.public_id);
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        setCopied(await copyToClipboard(link));
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <div className="qa-modal-overlay" role="presentation" onClick={onClose}>
            <div className="qa-modal qa-modal--qr" role="dialog" aria-modal="true" aria-label="QR-код для участников" onClick={(e) => e.stopPropagation()}>
                <h2>{session.title}</h2>
                <div className="qa-share__qr"><QRCodeSVG value={link} size={240} level="M" /></div>
                <div className="qa-share__row">
                    <input type="text" readOnly value={link} onFocus={(e) => e.target.select()} aria-label="Ссылка для участников" />
                    <button type="button" className="btn btn--secondary btn--sm" onClick={copy}>{copied ? 'Скопировано' : 'Копировать'}</button>
                </div>
                <p className="qa-hint">Покажите QR на слайде или откройте «Экран для проектора» — там QR уже есть.</p>
                <div className="qa-modal__footer">
                    <button type="button" className="btn btn--secondary" onClick={onClose}>Закрыть</button>
                </div>
            </div>
        </div>
    );
}

/** Раздел «Вопросы из зала»: список сессий, создание новой. */
function QaSessions() {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState(null);
    const [error, setError] = useState('');
    const [locked, setLocked] = useState(null);
    const [creating, setCreating] = useState(false);
    const [qrFor, setQrFor] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/qa-sessions');
            setSessions(res.data);
            setError('');
        } catch (err) {
            if (err.response?.status === 403) setLocked(getApiErrorMessage(err, ''));
            else setError(getApiErrorMessage(err, 'Не удалось загрузить сессии'));
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { document.title = 'Вопросы из зала — СВОД'; }, []);

    const remove = async (session) => {
        const count = session.counts.total + session.counts.waiting;
        if (!window.confirm(`Удалить сессию «${session.title}»${count ? ` и ${count} ${questionsWord(count)}` : ''}? Ссылка и QR-код перестанут работать.`)) return;
        try {
            await api.delete(`/qa-sessions/${session.id}`);
            notifyCountsChanged();
            load();
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось удалить сессию'));
        }
    };

    let content;
    if (locked !== null) {
        content = <QaLocked message={locked} />;
    } else {
        content = (
            <div className="qa-sessions">
                <div className="qa-sessions__head">
                    <div>
                        <h1>Вопросы из зала</h1>
                        <p>Отдельная ссылка и QR-код для вопросов спикеру — без регистрации участников и без опроса.</p>
                    </div>
                    {sessions && sessions.length > 0 && (
                        <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>+ Новая сессия</button>
                    )}
                </div>
                {error && <p className="qa-form-error" role="alert">{error}</p>}
                {sessions === null && !error && <div className="qa-loading">Загрузка…</div>}
                {sessions && sessions.length === 0 && (
                    <div className="qa-card qa-empty-state">
                        <h2>Соберите вопросы к выступлению</h2>
                        <ol className="qa-steps">
                            <li><b>Создайте сессию</b> — дайте ей название, например «Лекция 14 октября».</li>
                            <li><b>Покажите QR-код</b> на слайде или экране — участники задают вопросы с телефона и голосуют за интересные.</li>
                            <li><b>Ведите сессию</b> — выводите вопрос на экран, отмечайте отвеченные, после выступления скачайте все вопросы в Excel.</li>
                        </ol>
                        <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>Создать первую сессию</button>
                    </div>
                )}
                {sessions && sessions.length > 0 && (
                    <ul className="qa-session-list">
                        {sessions.map((s) => (
                            <li key={s.id} className="qa-card qa-session">
                                <div className="qa-session__top">
                                    <button type="button" className="qa-session__title" onClick={() => navigate(`/qa-host/${s.id}`)}>{s.title}</button>
                                    <span className={`qa-session__status${s.settings.enabled ? ' is-open' : ''}`}>
                                        {s.settings.enabled ? '● Приём открыт' : 'Приём закрыт'}
                                    </span>
                                </div>
                                <div className="qa-session__meta">
                                    <span>{s.counts.total} {questionsWord(s.counts.total)}</span>
                                    {s.counts.answered > 0 && <span>отвечено {s.counts.answered}</span>}
                                    {s.counts.waiting > 0 && <span className="qa-session__waiting">ждут модерации: {s.counts.waiting}</span>}
                                    {s.created_at && <span>{new Date(s.created_at).toLocaleDateString('ru-RU')}</span>}
                                </div>
                                <div className="qa-session__actions">
                                    <button type="button" className="btn btn--primary btn--sm" onClick={() => navigate(`/qa-host/${s.id}`)}>Открыть</button>
                                    <a className="btn btn--secondary btn--sm" href={`/qa/${s.public_id}/screen`} target="_blank" rel="noreferrer"><svg className="qa-icon" viewBox="0 0 24 24" aria-hidden><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></svg>Экран</a>
                                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => setQrFor(s)}>QR-код</button>
                                    <button type="button" className="qa-session__delete" onClick={() => remove(s)} aria-label={`Удалить «${s.title}»`} title="Удалить">Удалить</button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    }

    return (
        <AppShell section="qa" title="Вопросы из зала">
            {content}
            {creating && <CreateModal onClose={() => setCreating(false)} onCreated={(s) => navigate(`/qa-host/${s.id}`)} />}
            {qrFor && <QrModal session={qrFor} onClose={() => setQrFor(null)} />}
        </AppShell>
    );
}

export default QaSessions;
